import styled from 'styled-components'
import { useEffect, useState } from 'react'
import { Box, Button, Card, Center, Input, Table, Text, UserAvatar } from 'components'
import { useStateValue } from 'providers'

import { useGetQuery, usePostQuery } from 'hooks'
import { getUserName } from 'utils'

const EditActions = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0 0.5rem 0.5rem;
`

const InlineInput = styled.input`
  border: 1px solid ${({ theme: { colors } }) => colors('chrome')};
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.9rem;
  background: transparent;
  color: ${({ theme: { colors } }) => colors('text')};
  outline: none;
  width: 100%;
`

export const UserLocations = ({ locations }) => {
  if (!locations || locations.length === 0) return null
  return <Table
          data={locations}
          columns={[
            { title: 'Name', key: 'name' },
            { title: 'Status', key: 'status' }
          ]}
          title="User Locations"
  />
}

export const UserApiKeys = () => {
  const { data, refetch } = useGetQuery({ url: '/api_keys' })
  const { post: createKey } = usePostQuery({ url: '/api_keys' })
  const { post: revokeKey } = usePostQuery({ method: 'PUT' })
  const { post: deleteKey } = usePostQuery({ method: 'DELETE' })

  const [newName, setNewName] = useState('')
  const [revealedKey, setRevealedKey] = useState(null)

  const rows = data?.api_keys || []

  const addKey = () => {
    if (!newName.trim()) return
    createKey({ name: newName.trim() }).then((result) => {
      if (result?.api_key) {
        setNewName('')
        setRevealedKey(result.api_key)
        refetch(true)
      }
    })
  }

  const toggleActive = (key, active) => {
    revokeKey({ active }, `/api_keys/${key.id}`).then(() => refetch(true))
  }

  const removeKey = (key) => {
    deleteKey({}, `/api_keys/${key.id}`).then(() => refetch(true))
  }

  return (
    <Box margin="0.5rem">
      <Card>
        {revealedKey && (
          <Box css="margin:0.5rem 1rem;padding:0.75rem;border-radius:0.25rem;background:rgba(0,0,0,0.05);">
            <Text size="0.85rem">
              Copy this key now — it will not be shown again.
            </Text>
            <Text size="0.9rem" style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {revealedKey.key}
            </Text>
            <Button onPress={() => setRevealedKey(null)}>Done</Button>
          </Box>
        )}
        <Box css="display:flex;align-items:center;gap:0.4rem;margin:0.5rem 1rem;">
          <Input
            name="new-api-key-name"
            placeholder="Key name (e.g. my script)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <Button onPress={addKey} disabled={!newName.trim()}>Create key</Button>
        </Box>
        <Table
          data={rows}
          title="API Keys"
          columns={[
            { title: 'Name', key: 'name' },
            {
              title: 'Last used',
              key: 'last_used_at',
              render: (v) => v ? new Date(v).toLocaleDateString() : 'Never'
            },
            {
              title: 'Status',
              key: 'active',
              render: (active) => active ? 'Active' : 'Revoked'
            },
            {
              title: 'Action',
              key: 'id',
              className: 'right',
              render: (id, _i, row) => (
                <Box css="display:flex;gap:0.4rem;justify-content:flex-end;">
                  <Button onPress={() => toggleActive(row, !row.active)}>
                    {row.active ? 'Revoke' : 'Reactivate'}
                  </Button>
                  <Button onPress={() => removeKey(row)}>Delete</Button>
                </Box>
              )
            }
          ]}
        />
      </Card>
    </Box>
  )
}

export const Account = () => {
  const [rows, setRows] = useState([])
  const [editMode, setEditMode] = useState(false)
  const [editUsername, setEditUsername] = useState('')
  const [saveError, setSaveError] = useState(null)
  const [{ session: { user } }, dispatch] = useStateValue()

  const { data, isFetching, reFetch } = useGetQuery({ url: `/account/${user.slug}` })
  const { post: updateUser, isFetching: isUpdating } = usePostQuery({
    method: 'PUT',
    url: `/account/${user.slug}/edit`
  })

  useEffect(() => {
    if (!data) return
    const keys = Object.keys(data.user).map((key) => ({
      key,
      val: JSON.stringify(data.user[key])
    }))
    keys.push({ key: 'role', val: data.user.roles?.[0]?.name })
    const rows = keys.filter((row) =>
      ['email', 'full_name', 'username', 'role'].indexOf(row.key) > -1
    )
    setRows(rows)
    setEditUsername(data.user.username || '')
  }, [data])

  const startEdit = () => {
    setSaveError(null)
    setEditMode(true)
  }

  const cancelEdit = () => {
    setEditUsername(data?.user?.username || '')
    setSaveError(null)
    setEditMode(false)
  }

  const saveEdit = async () => {
    setSaveError(null)
    const result = await updateUser({ username: editUsername })
    if (result?.user) {
      setEditMode(false)
      dispatch({ type: 'LOGIN', data: { user: result.user } })
      reFetch(true)
    } else {
      setSaveError('Username is already taken.')
    }
  }

  if (isFetching) return <div>Loading...</div>

  const valColumn = {
    title: 'Value',
    key: 'val',
    render: (val, _i, row) => {
      if (editMode && row.key === 'username') {
        return (
          <InlineInput
            value={editUsername}
            onChange={(e) => setEditUsername(e.target.value)}
            placeholder="Choose a username"
            autoFocus
          />
        )
      }
      return val
    }
  }

  return (
    <Box margin="0.5rem">
      <Card>
        <Center>
          <UserAvatar
            className="rainbow"
            editable
            onUploaded={() => reFetch(true)}
            size="large"
            user={data?.user}
          ></UserAvatar>
        </Center>
        <Table
          data={rows || {}}
          columns={[
            { title: 'Field', key: 'key' },
            valColumn
          ]}
        />
        {saveError && (
          <Text size="0.85rem" color="error" style={{ padding: '0 0.5rem' }}>{saveError}</Text>
        )}
        <EditActions>
          {!editMode
            ? <Button onPress={startEdit}>Edit</Button>
            : <>
                <Button onPress={saveEdit} disabled={isUpdating || !editUsername.trim()}>Save</Button>
                <Button onPress={cancelEdit}>Cancel</Button>
              </>
          }
        </EditActions>
        <UserLocations locations={data?.user?.user_locations} />
      </Card>
      <UserApiKeys />
    </Box>
  )
}
